import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Star,
  MapPin,
  Phone,
  Mail,
  Globe,
  Users,
  BookOpen,
  Award,
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
  Share2,
  Calendar,
  TrendingUp,
  Plus,
  Send
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import OptimizedImage from '../components/OptimizedImage';
import { collegeService, reviewService } from '../lib/database';
import { collegeFAQService } from '../lib/database';
import type { College, Review } from '../lib/supabase';
import type { CollegeFAQ } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const PUBLIC_ORIGIN =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_PUBLIC_URL
    ? String(import.meta.env.VITE_PUBLIC_URL)
    : 'https://gradvikalp.com';

const CollegeDetails: React.FC = () => {
  const { slugOrId } = useParams<{ slugOrId: string }>();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [college, setCollege] = useState<College | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [faqs, setFaqs] = useState<CollegeFAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [averageRating, setAverageRating] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [newReview, setNewReview] = useState({
    author_name: '',
    course: '',
    year: '',
    rating: 5,
    title: '',
    content: ''
  });

  const fetchCollegeData = useCallback(async () => {
    if (!slugOrId) return;

    try {
      setLoading(true);
      setError(null);

      let collegeData: College;

      // Try to fetch by slug first, then by ID if slug fails
      try {
        collegeData = await collegeService.getBySlug(slugOrId);
      } catch (slugError) {
        collegeData = await collegeService.getById(slugOrId);
      }

      setCollege(collegeData);

      // Fetch reviews for this college
      const reviewsData = await reviewService.getByCollegeId(collegeData.id);
      setReviews(reviewsData || []);

      // Fetch FAQs for this college
      try {
        const faqData = await collegeFAQService.getByCollegeId(collegeData.id);
        setFaqs(faqData);
      } catch (faqError) {
        console.error('Error fetching college FAQs:', faqError);
        // Don't show error for FAQs as it's not critical
      }
    } catch (err: any) {
      console.error('Error fetching college data:', err);
      setError('College not found');
      toast.error('College not found');
    } finally {
      setLoading(false);
    }
  }, [slugOrId]);

  useEffect(() => {
    fetchCollegeData();
  }, [fetchCollegeData]);

  useEffect(() => {
    if (reviews.length > 0) {
      const avg = reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length;
      setAverageRating(Math.round(avg * 10) / 10);
    }
  }, [reviews]);

  const handleHelpfulClick = async (reviewId: string, increment = true) => {
    try {
      await reviewService.updateHelpfulCount(reviewId, increment);
      setReviews((prev) =>
        prev.map((review) =>
          review.id === reviewId
            ? { ...review, helpful_count: review.helpful_count + (increment ? 1 : -1) }
            : review
        )
      );
      toast.success(increment ? 'Marked as helpful' : 'Removed helpful vote');
    } catch (err) {
      console.error('Error updating helpful count:', err);
      toast.error('Failed to update vote');
    }
  };

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!college) return;
    
    // Validation
    if (!newReview.author_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!newReview.course.trim()) {
      toast.error('Please enter your course');
      return;
    }
    if (!newReview.year.trim()) {
      toast.error('Please enter your year');
      return;
    }
    if (!newReview.title.trim()) {
      toast.error('Please enter a review title');
      return;
    }
    if (!newReview.content.trim()) {
      toast.error('Please enter your review');
      return;
    }
    if (newReview.content.length < 50) {
      toast.error('Please write a more detailed review (at least 50 characters)');
      return;
    }

    setSubmittingReview(true);
    
    try {
      const reviewData = {
        college_id: college.id,
        user_id: user?.id?.startsWith('demo-') ? null : user?.id || null,
        author_name: newReview.author_name.trim(),
        course: newReview.course.trim(),
        year: newReview.year.trim(),
        rating: newReview.rating,
        title: newReview.title.trim(),
        content: newReview.content.trim(),
        approved: false // Reviews need approval
      };

      await reviewService.create(reviewData);
      
      toast.success('Review submitted successfully! It will be published after approval.');
      
      // Reset form
      setNewReview({
        author_name: '',
        course: '',
        year: '',
        rating: 5,
        title: '',
        content: ''
      });
      setShowReviewForm(false);
      
      // Refresh reviews (though new review won't show until approved)
      const reviewsData = await reviewService.getByCollegeId(college.id);
      setReviews(reviewsData || []);
      
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  const getRedirectUrl = () => {
    if (!college) return '/personalized-list';
    if (!college) return '/neet-college-predictor';
    
    // Redirect based on college category
    if (college.category.toLowerCase().includes('medical') || 
        college.category.toLowerCase().includes('mbbs') ||
        college.stream?.toLowerCase().includes('medical')) {
      return '/neet-college-predictor'; // NEET Predictor
    } else if (college.category.toLowerCase().includes('engineering') ||
               college.category.toLowerCase().includes('technology') ||
               college.stream?.toLowerCase().includes('engineering')) {
      return '/josaa-personalized-list'; // JoSAA Predictor
    }
    
    // Default to NEET predictor for other categories
    return '/neet-college-predictor';
  };
  const shareCollege = async () => {
    if (!college) return;

    const fullUrl = `${PUBLIC_ORIGIN}/college/${college.slug || ''}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${college.name} - College Details & Reviews`,
          text: college.description,
          url: fullUrl,
        });
      } catch (_err) {
        await navigator.clipboard.writeText(fullUrl);
        toast.success('Link copied to clipboard!');
      }
    } else {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'courses', label: 'Courses' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'facilities', label: 'Facilities' },
  ];

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading college details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !college) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light">
        <div className="container py-5">
          {/* Back Button */}
          <Link to="/college-reviews" className="btn btn-link text-primary text-decoration-none mb-4 p-0">
            <ArrowLeft className="me-2" size={16} />
            Back to College Reviews
          </Link>

          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
                    <Award className="h-6 w-6 text-danger" />
                  </div>
                </div>
                <h1 className="h2 text-dark mb-3">College Not Found</h1>
                <p className="text-muted mb-4">
                  The college you're looking for doesn't exist or may have been removed. This could be because the college data
                  hasn't been added to the database yet.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/college-reviews" className="btn btn-primary">
                    Browse All Colleges
                  </Link>
                  <Link to="/contact" className="btn btn-outline-primary">
                    Report Issue
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light">
      <SEOHead
        title={college?.meta_title || `${college?.name} - College Details & Reviews | Grad Vikalp`}
        description={college?.meta_description || `Complete guide to ${college?.name} - ${college?.type} ${college?.category} college in ${college?.location}, ${college?.state}. Get fees, cutoffs, reviews & admission details.`}
        keywords={college?.meta_keywords || `${college?.name}, ${college?.category} college, ${college?.location}, ${college?.state}, college reviews, admission, fees, cutoffs`}
        url={`/college/${college.slug}`}
        type="college"
        image={college.og_image || college.image_url}
      />
      <div className="container py-4">
        {/* Back Button */}
        <Link to="/college-reviews" className="btn btn-link text-primary text-decoration-none mb-4 p-0">
          <ArrowLeft className="me-2" size={16} />
          Back to College Reviews
        </Link>

        {/* College Header */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="position-relative">
            <OptimizedImage
              src={college.image_url}
              alt={`${college.name} campus view - Premier ${college.category} college in ${college.location}, ${college.state} established in ${college.established}`}
              title={`${college.name} Campus | ${college.type} ${college.category} College | Student Rating: ${college.rating}/5 | Fees: ${college.fees} | Established: ${college.established}`}
              className="card-img-top"
              style={{ height: '300px', objectFit: 'cover' }}
              priority={true}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
            />
            <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark bg-opacity-50"></div>
            <div className="position-absolute bottom-0 start-0 p-4 text-white">
              <div className="mb-2">
                <span className="badge bg-primary fs-6 px-3 py-2 me-2">{college.category}</span>
                <span className="badge bg-success fs-6 px-3 py-2">{college.type}</span>
              </div>
              <h1 className="display-5 fw-bold mb-3">{college.name}</h1>
              <div className="d-flex align-items-center gap-4 flex-wrap">
                <div className="d-flex align-items-center gap-1">
                  <Star className="star-rating" size={20} fill="currentColor" />
                  <span className="fw-semibold">{averageRating || college.rating}</span>
                  <span className="opacity-75">({reviews.length} reviews)</span>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <MapPin size={16} />
                  <span>
                    {college.location}, {college.state}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="card-body p-4">
            <div className="row g-4 text-center">
              <div className="col-6 col-md-3">
                <Calendar className="text-primary mb-2" size={24} />
                <div className="fw-semibold text-dark">{college.established}</div>
                <div className="small text-muted">Established</div>
              </div>
              <div className="col-6 col-md-3">
                <BookOpen className="text-success mb-2" size={24} />
                <div className="fw-semibold text-dark">{college.courses?.length || 0}</div>
                <div className="small text-muted">Courses</div>
              </div>
              <div className="col-6 col-md-3">
                <Users className="text-info mb-2" size={24} />
                <div className="fw-semibold text-dark">{reviews.length}</div>
                <div className="small text-muted">Student Reviews</div>
              </div>
              <div className="col-6 col-md-3">
                <Star className="text-warning mb-2" size={24} />
                <div className="fw-semibold text-dark">{averageRating || college.rating}/5</div>
                <div className="small text-muted">Average Rating</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="row g-2 mt-4">
              <div className="col-md-4">
                <button
                  onClick={shareCollege}
                  className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                  type="button"
                >
                  <Share2 size={16} />
                  Share College
                </button>
              </div>
              <div className="col-md-4">
                <Link to={getRedirectUrl()} className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2">
                  <TrendingUp size={16} />
                  Get Recommendations
                </Link>
              </div>
              <div className="col-md-4">
                <Link to="/college-reviews" className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2">
                  <BookOpen size={16} />
                  Explore Colleges
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="card border-0 shadow-lg">
          <div className="card-header bg-white border-bottom">
            <ul className="nav nav-tabs card-header-tabs">
              {tabs.map((tab) => (
                <li key={tab.id} className="nav-item">
                  <button onClick={() => setActiveTab(tab.id)} className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}>
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-body p-4">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                {/* College Highlights */}
                <div className="alert alert-info mb-4">
                  <h2 className="h5 fw-bold mb-2">🏛️ College Highlights</h2>
                  <p className="mb-0">
                    {college.name} is a {college.type.toLowerCase()} {college.category.toLowerCase()} institution located in {college.location},{' '}
                    {college.state}, established in {college.established}. Known for its quality education and excellent facilities.
                  </p>
                </div>

                <div className="mb-4">
                  <h2 className="h3 fw-bold text-dark mb-3">About {college.name}</h2>
                  <p className="text-muted">{college.description}</p>
                  <div className="mt-3">
                    <h3 className="h5 fw-semibold text-dark mb-2">🌟 Key Highlights:</h3>
                    <div className="d-flex flex-wrap gap-2">
                      <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">{college.type} Institution</span>
                      <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">Established {college.established}</span>
                      <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">{college.category} Programs</span>
                      <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">
                        {college.location}, {college.state}
                      </span>
                      <span className="badge bg-success bg-opacity-10 text-success fs-6 px-3 py-2">
                        {averageRating || college.rating}/5 Rating
                      </span>
                    </div>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-md-6">
                    <h3 className="h4 fw-semibold text-dark mb-3">📋 College Information</h3>
                    <div className="table-responsive">
                      <table className="table table-borderless">
                        <tbody>
                          <tr>
                            <td className="text-muted">Established:</td>
                            <td className="fw-medium">{college.established}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Type:</td>
                            <td className="fw-medium">{college.type}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Category:</td>
                            <td className="fw-medium">{college.category}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Annual Fees:</td>
                            <td className="fw-medium">{college.fees}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Admission Cutoff:</td>
                            <td className="fw-medium">{college.cutoff}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Student Rating:</td>
                            <td className="fw-medium">
                              <div className="d-flex align-items-center gap-1">
                                <Star className="star-rating" size={16} fill="currentColor" />
                                <span>{averageRating || college.rating}/5</span>
                                <span className="text-muted">({reviews.length} reviews)</span>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <h3 className="h4 fw-semibold text-dark mb-3">📞 Contact Information</h3>
                    <div className="vstack gap-3">
                      <div className="d-flex align-items-center gap-2">
                        <MapPin size={16} className="text-muted" />
                        <span className="text-muted">
                          {college.location}, {college.state}
                        </span>
                      </div>
                      {college.contact_phone && (
                        <div className="d-flex align-items-center gap-2">
                          <Phone size={16} className="text-muted" />
                          <a href={`tel:${college.contact_phone}`} className="text-primary text-decoration-none">
                            {college.contact_phone}
                          </a>
                        </div>
                      )}
                      {college.contact_email && (
                        <div className="d-flex align-items-center gap-2">
                          <Mail size={16} className="text-muted" />
                          <a href={`mailto:${college.contact_email}`} className="text-primary text-decoration-none">
                            {college.contact_email}
                          </a>
                        </div>
                      )}
                      {college.contact_website && (
                        <div className="d-flex align-items-center gap-2">
                          <Globe size={16} className="text-muted" />
                          <a
                            href={college.contact_website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none"
                          >
                            Official Website
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Admission Information */}
                <div className="card bg-light border-0 mt-4">
                  <div className="card-body p-4">
                    <h3 className="h4 fw-bold text-dark mb-3">🎓 Admission Information</h3>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="text-center">
                          <div className="h5 fw-bold text-primary">{college.cutoff}</div>
                          <div className="small text-muted">Admission Cutoff</div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-center">
                          <div className="h5 fw-bold text-success">{college.fees}</div>
                          <div className="small text-muted">Annual Fees</div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-center">
                          <div className="h5 fw-bold text-info">{college.type}</div>
                          <div className="small text-muted">College Type</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div>
                <h2 className="h3 fw-bold text-dark mb-4">📚 Available Courses at {college.name}</h2>
                {college.courses && college.courses.length > 0 ? (
                  <>
                    <div className="alert alert-info mb-4">
                      <h3 className="h5 fw-bold mb-2">🎯 Course Offerings</h3>
                      <p className="mb-0">
                        {college.name} offers {college.courses.length} different courses across various disciplines. All courses are designed
                        to provide comprehensive education and excellent career prospects.
                      </p>
                    </div>
                    <div className="row g-3">
                      {college.courses.map((course, index) => (
                        <div key={index} className="col-md-4">
                          <div className="card border h-100">
                            <div className="card-body text-center">
                              <BookOpen className="text-primary mb-2" size={24} />
                              <h4 className="h6 fw-semibold text-dark mb-2">{course}</h4>
                              <p className="small text-muted mb-0">
                                {college.category} program offered at {college.name}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-muted">No course information available.</p>
                )}

                {college.specializations && college.specializations.length > 0 && (
                  <div className="mt-5">
                    <h3 className="h4 fw-semibold text-dark mb-3">🔬 Specializations Available</h3>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {college.specializations.map((spec, index) => (
                        <span key={index} className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">
                          {spec}
                        </span>
                      ))}
                    </div>
                    <p className="text-muted small">
                      {college.name} offers {college.specializations.length} specialized programs designed to meet industry demands and provide
                      students with focused expertise in their chosen fields.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h2 className="h3 fw-bold text-dark mb-1">⭐ Student Reviews ({reviews.length})</h2>
                    <p className="text-muted mb-0">Authentic reviews from students and alumni of {college.name}</p>
                  </div>
                  <button 
                    onClick={() => setShowReviewForm(!showReviewForm)}
                    className="btn btn-primary d-flex align-items-center gap-2"
                  >
                    <Plus size={16} />
                    {showReviewForm ? 'Cancel' : 'Write a Review'}
                  </button>
                </div>

                {/* Review Form */}
                {showReviewForm && (
                  <div className="card border-0 shadow-lg mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">Write a Review for {college.name}</h5>
                    </div>
                    <div className="card-body p-4">
                      <form onSubmit={handleReviewSubmit}>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Your Name *</label>
                            <input
                              type="text"
                              value={newReview.author_name}
                              onChange={(e) => setNewReview(prev => ({ ...prev, author_name: e.target.value }))}
                              className="form-control"
                              placeholder="Enter your full name"
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Course/Program *</label>
                            <input
                              type="text"
                              value={newReview.course}
                              onChange={(e) => setNewReview(prev => ({ ...prev, course: e.target.value }))}
                              className="form-control"
                              placeholder="e.g., B.Tech CSE, MBBS"
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Year of Study/Graduation *</label>
                            <select
                              value={newReview.year}
                              onChange={(e) => setNewReview(prev => ({ ...prev, year: e.target.value }))}
                              className="form-select"
                              required
                            >
                              <option value="">Select Year</option>
                              <option value="1st Year">1st Year</option>
                              <option value="2nd Year">2nd Year</option>
                              <option value="3rd Year">3rd Year</option>
                              <option value="4th Year">4th Year</option>
                              <option value="Final Year">Final Year</option>
                              <option value="Alumni 2024">Alumni 2024</option>
                              <option value="Alumni 2023">Alumni 2023</option>
                              <option value="Alumni 2022">Alumni 2022</option>
                              <option value="Alumni 2021">Alumni 2021</option>
                              <option value="Alumni (Older)">Alumni (Older)</option>
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Overall Rating *</label>
                            <select
                              value={newReview.rating}
                              onChange={(e) => setNewReview(prev => ({ ...prev, rating: parseInt(e.target.value) }))}
                              className="form-select"
                              required
                            >
                              <option value={5}>⭐⭐⭐⭐⭐ Excellent (5/5)</option>
                              <option value={4}>⭐⭐⭐⭐ Good (4/5)</option>
                              <option value={3}>⭐⭐⭐ Average (3/5)</option>
                              <option value={2}>⭐⭐ Below Average (2/5)</option>
                              <option value={1}>⭐ Poor (1/5)</option>
                            </select>
                          </div>
                          <div className="col-12">
                            <label className="form-label fw-semibold">Review Title *</label>
                            <input
                              type="text"
                              value={newReview.title}
                              onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
                              className="form-control"
                              placeholder="e.g., Great college with excellent faculty"
                              required
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label fw-semibold">Your Review *</label>
                            <textarea
                              value={newReview.content}
                              onChange={(e) => setNewReview(prev => ({ ...prev, content: e.target.value }))}
                              className="form-control"
                              rows={5}
                              placeholder="Share your experience about academics, faculty, infrastructure, placements, campus life, etc. (minimum 50 characters)"
                              required
                            />
                            <div className="form-text">
                              {newReview.content.length}/500 characters (minimum 50 required)
                            </div>
                          </div>
                        </div>
                        
                        <div className="d-flex gap-2 mt-4">
                          <button
                            type="submit"
                            disabled={submittingReview}
                            className="btn btn-primary d-flex align-items-center gap-2"
                          >
                            {submittingReview ? (
                              <>
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Send size={16} />
                                Submit Review
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowReviewForm(false)}
                            className="btn btn-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                        
                        <div className="alert alert-info mt-3 mb-0">
                          <small>
                            <strong>Note:</strong> Your review will be published after verification to ensure authenticity and quality.
                          </small>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                {/* Review Summary */}
                {reviews.length > 0 && (
                  <div className="card bg-light border-0 mb-4">
                    <div className="card-body p-4">
                      <h3 className="h5 fw-bold text-dark mb-3">📊 Review Summary</h3>
                      <div className="row g-3 text-center">
                        <div className="col-md-3">
                          <div className="h4 fw-bold text-primary">{averageRating}/5</div>
                          <div className="small text-muted">Average Rating</div>
                        </div>
                        <div className="col-md-3">
                          <div className="h4 fw-bold text-success">{reviews.length}</div>
                          <div className="small text-muted">Total Reviews</div>
                        </div>
                        <div className="col-md-3">
                          <div className="h4 fw-bold text-info">
                            {Math.round((reviews.filter((r) => r.rating >= 4).length / reviews.length) * 100)}%
                          </div>
                          <div className="small text-muted">Positive Reviews</div>
                        </div>
                        <div className="col-md-3">
                          <div className="h4 fw-bold text-warning">
                            {reviews.reduce((sum, r) => sum + (r.helpful_count || 0), 0)}
                          </div>
                          <div className="small text-muted">Helpful Votes</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {reviews.length > 0 ? (
                  <div className="vstack gap-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="card border">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <h4 className="h6 fw-semibold text-dark mb-0">{review.author_name}</h4>
                                <span className="text-muted">•</span>
                                <span className="text-muted small">
                                  {review.course} • {review.year}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    size={16}
                                    className={i < review.rating ? 'star-rating' : 'text-muted'}
                                    fill="currentColor"
                                  />
                                ))}
                                <span className="ms-2 fw-semibold">{review.rating}/5</span>
                              </div>
                            </div>
                            <span className="text-muted small">
                              {new Date(review.created_at).toLocaleDateString()}
                            </span>
                          </div>

                          <h5 className="h6 fw-semibold text-dark mb-2">{review.title}</h5>
                          <p className="text-muted mb-3">{review.content}</p>

                          <div className="d-flex align-items-center gap-3">
                            <button
                              onClick={() => handleHelpfulClick(review.id, true)}
                              className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                            >
                              <ThumbsUp size={14} />
                              <span>Helpful ({review.helpful_count})</span>
                            </button>
                            <button
                              onClick={() => handleHelpfulClick(review.id, false)}
                              className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                            >
                              <ThumbsDown size={14} />
                              <span>Not Helpful</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <BookOpen className="text-muted mx-auto mb-3" size={64} />
                    </div>
                    <h4 className="h5 text-dark mb-3">No reviews yet</h4>
                    <p className="text-muted mb-4">Be the first to share your experience at {college.name}!</p>
                    <button 
                      onClick={() => setShowReviewForm(true)}
                      className="btn btn-primary d-flex align-items-center gap-2 mx-auto"
                    >
                      <Plus size={16} />
                      Write First Review
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Facilities Tab */}
            {activeTab === 'facilities' && (
              <div>
                <h2 className="h3 fw-bold text-dark mb-4">🏢 Campus Facilities at {college.name}</h2>
                {college.facilities && college.facilities.length > 0 ? (
                  <>
                    <div className="alert alert-success mb-4">
                      <h3 className="h5 fw-bold mb-2">🌟 World-Class Facilities</h3>
                      <p className="mb-0">
                        {college.name} provides {college.facilities.length} modern facilities to ensure students have access to the best resources for their academic and personal development.
                      </p>
                    </div>
                    <div className="row g-3">
                      {college.facilities.map((facility, index) => (
                        <div key={index} className="col-lg-3 col-md-4 col-sm-6">
                          <div className="card bg-light border-0 text-center h-100">
                            <div className="card-body py-3 d-flex flex-column justify-content-center">
                              <Award className="text-primary mx-auto mb-2" size={24} />
                              <div className="fw-medium text-dark">{facility}</div>
                              <div className="small text-muted mt-1">Available on campus</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-muted">No facility information available.</p>
                )}
              </div>
            )}
          </div>

          {/* FAQ Section for SEO */}
          {faqs.length > 0 && (
            <div className="card bg-light border-0 mt-4">
              <div className="card-body p-4">
                <h2 className="h3 fw-bold text-dark mb-4">❓ Frequently Asked Questions about {college.name}</h2>
                <div className="accordion" id="collegeAccordion">
                  {faqs.map((faq, index) => (
                    <div key={faq.id} className="accordion-item border-0 bg-transparent">
                      <h3 className="accordion-header">
                        <button 
                          className={`accordion-button ${index !== 0 ? 'collapsed' : ''} bg-transparent border-0 fw-semibold`} 
                          type="button" 
                          data-bs-toggle="collapse" 
                          data-bs-target={`#collegeFaq${index + 1}`}
                        >
                          {faq.question}
                        </button>
                      </h3>
                      <div 
                        id={`collegeFaq${index + 1}`} 
                        className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`} 
                        data-bs-parent="#collegeAccordion"
                      >
                        <div className="accordion-body">
                          {faq.answer}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="card bg-primary text-white border-0 mt-4">
            <div className="card-body p-4 text-center">
              <h2 className="h3 fw-bold mb-3">🎯 Interested in {college.name}?</h2>
              <p className="mb-4">
                Get personalized college recommendations based on your academic profile and preferences.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                <Link to={getRedirectUrl()} className="btn btn-light text-primary fw-semibold">
                  {college.category.toLowerCase().includes('medical') ? 'Get NEET Predictions' : 
                   college.category.toLowerCase().includes('engineering') ? 'Get JEE Predictions' : 
                   'Get Recommendations'}
                </Link>
                <Link to="/college-reviews" className="btn btn-outline-light">
                  Explore More Colleges
                </Link>
              </div>
            </div>
          </div>

          {/* Structured Data for College */}
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'EducationalOrganization',
              name: college.name,
              description: college.description,
              url: `${PUBLIC_ORIGIN}/college/${college.slug || ''}`,
              image: college.image_url,
              address: {
                '@type': 'PostalAddress',
                addressLocality: college.location,
                addressRegion: college.state,
                addressCountry: 'IN',
              },
              foundingDate: college.established,
              telephone: college.contact_phone,
              email: college.contact_email,
              sameAs: college.contact_website,
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: averageRating || college.rating,
                reviewCount: reviews.length,
                bestRating: 5,
                worstRating: 1,
              },
              review: reviews.slice(0, 5).map((review) => ({
                '@type': 'Review',
                author: {
                  '@type': 'Person',
                  name: review.author_name,
                },
                datePublished: review.created_at,
                reviewBody: review.content,
                name: review.title,
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: review.rating,
                  bestRating: 5,
                  worstRating: 1,
                },
              })),
              offers: {
                '@type': 'Offer',
                category: college.category,
                price: college.fees,
                priceCurrency: 'INR',
              },
            })}
          </script>
        </div>
      </div>
    </div>
  );
};

export default CollegeDetails;
